import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ControlledDateField, ControlledSelectField, ControlledTextField } from 'components/Form';
import { GroupHeader } from 'components/GroupHeader';
import { ModalDialog } from 'components/ModalDialog';
import { RequestStatus } from 'features/request/requestSlice';
import type { BimCalculationData, BimWorkOrderData, BimWorkOrderSeasonData } from 'gql/graphql';
import { WorkOrderStatusEnum, ContractStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { getWorkOrderStatusLabel } from 'pages/Project/UerProject/WorkOrder/workOrderConstants';
import { cnMixSpace, Grid, GridItem, IconCalendar } from 'uikit';
import { requiredBaseRule } from 'utils/constants';

import type { PayloadAction } from '@reduxjs/toolkit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useGetChangeStatusDialogText } from './useGetChangeStatusDialogText';
import { useTransformRequisitesData } from './useTransformRequisitesData';

interface IBimOrderRequisitesUpdate {
    isFrameContract: boolean;
    defaultValues: BimWorkOrderData | BimCalculationData | null | undefined;
    isLoading: boolean;
    onSuccess: () => void;
    onCancel: () => void;
    contractStatus?: ContractStatusEnum;
    orderRules?: IUseAccess['orderBim'];
    workOrderId: number;
    bimWorkOrderVersionsRequest: (value: { workOrderId: number }) => PayloadAction;
}

export const BimOrderRequisitesUpdate = (props: IBimOrderRequisitesUpdate) => {
    const dispatch = useDispatch();
    const {
        defaultValues,
        onCancel,
        onSuccess,
        isFrameContract,
        isLoading,
        contractStatus,
        orderRules,
        bimWorkOrderVersionsRequest,
        workOrderId,
    } = props;

    const { id: projectId, orderId } = useParams();
    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const formRef = useRef<HTMLFormElement>(null);
    const [isOpenDialogStatus, setIsOpenDialogStatus] = useState<boolean>(false);

    const currentOrderStatus = defaultValues?.status;

    const {
        control,
        resetField,
        reset,
        formState: { dirtyFields, isDirty },
        handleSubmit,
        clearErrors,
    } = useForm({
        mode: 'onChange',
    });

    const status = useWatch({ control, name: 'status' });

    const {
        preparedDefaultRequisitesData,
        updateBimOrderRequisites,
        getTransformedData,
        isUpdating,
        updateOrderStatus,
    } = useTransformRequisitesData({
        defaultValues,
        isFrameContract,
        onSuccess,
    });

    useEffect(() => {
        if (preparedDefaultRequisitesData) {
            reset(preparedDefaultRequisitesData);
        }
    }, [preparedDefaultRequisitesData, reset]);

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onCancel,
        });

    const handleCancel = useCallback(() => {
        if (isDirty) {
            handleOpenConfirmationDialog();
        } else {
            onCancel?.();
        }
    }, [handleOpenConfirmationDialog, isDirty, onCancel]);

    const statusItems = useMemo(() => {
        const statusFollowers = (defaultValues?.statusFollowers || []).map((follower) => ({
            id: follower,
            label: getWorkOrderStatusLabel(follower),
        }));

        return [
            ...statusFollowers,
            ...(preparedDefaultRequisitesData?.status ? [preparedDefaultRequisitesData?.status] : []),
        ];
    }, [defaultValues?.statusFollowers, preparedDefaultRequisitesData?.status]);

    const confirmChangeStatusText = useGetChangeStatusDialogText({
        currentStatus: currentOrderStatus,
        nextStatus: status?.id,
        isFrameContract,
    });

    useEffect(() => {
        if (dirtyFields.status) {
            setIsOpenDialogStatus(true);
        }
    }, [dirtyFields.status, status]);

    const handleCancelDialogStatus = useCallback(() => {
        resetField('status');
        setIsOpenDialogStatus(false);
    }, [resetField]);

    const handleSubmitDialogStatus = useCallback(() => {
        setIsOpenDialogStatus(false);
    }, []);

    const isContractStatusDelayed = contractStatus === ContractStatusEnum.Delayed;
    const isContractStatusCompleted = contractStatus === ContractStatusEnum.Completed;

    const subjectAndInternalRequired = status?.id !== WorkOrderStatusEnum.OnFormation;
    const isWorkOrderStatusOnFormation = currentOrderStatus === WorkOrderStatusEnum.OnFormation;
    const isWorkOrderStatusCompleted = currentOrderStatus === WorkOrderStatusEnum.Completed;

    const saveHandler = useCallback(() => {
        formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }, []);

    const onSubmit = useCallback(
        (data) => {
            sendGpnAnalyticsEvent({
                componentId: 'rim_order save button_pressed',
                projectId,
                contractId: projectData?.contractId,
                orderId: Number(orderId),
                orderStatus: data.status.id,
                projectName: projectData?.name,
                projectType: projectData?.contractType,
                projectDO: projectData?.subsidiary?.name,
                methodology: projectData?.methodology,
            });

            dispatch(updateBimOrderRequisites(getTransformedData(data)));
        },
        [
            dispatch,
            getTransformedData,
            orderId,
            projectData?.contractId,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
            sendGpnAnalyticsEvent,
            updateBimOrderRequisites,
        ],
    );

    const modalTitle = useMemo(
        () => (isFrameContract ? 'Данные Наряд-Заказа' : 'Данные Расчета Договора'),
        [isFrameContract],
    );
    const modalStatusTitle = useMemo(
        () => (isFrameContract ? 'Смена статуса Наряд-Заказа' : 'Смена статуса Расчета Договора'),
        [isFrameContract],
    );

    const isDisabledFieldStatus =
        isWorkOrderStatusCompleted ||
        isContractStatusDelayed ||
        isContractStatusCompleted ||
        !orderRules?.canEditStatus;

    useEffect(() => {
        if (updateOrderStatus === RequestStatus.SUCCESS) {
            dispatch(bimWorkOrderVersionsRequest({ workOrderId }));
        }
    }, [bimWorkOrderVersionsRequest, dispatch, updateOrderStatus, workOrderId]);

    return (
        <ModalDialog
            isOpen
            title={modalTitle}
            onClose={handleCancel}
            onConfirm={saveHandler}
            bodyMinHeight="minContent"
            isOnClickOutsideDisable
            isLoading={isLoading}
            isCommit={isUpdating}
            btnConfirmDisabled={!isDirty}
            withTitleCloseButton
        >
            <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
                <Grid cols={1} style={{ width: 600, padding: 20 }}>
                    <GridItem>
                        {isFrameContract && <GroupHeader mB="s">Основная информация</GroupHeader>}
                        <ControlledSelectField
                            control={control}
                            label="Статус"
                            items={statusItems}
                            name="status"
                            className={cnMixSpace({ mB: 's' })}
                            disabled={isDisabledFieldStatus}
                        />
                        {isFrameContract && (
                            <>
                                <ControlledTextField
                                    control={control}
                                    label="Предмет"
                                    name="subject"
                                    tooltipSize="m"
                                    className={cnMixSpace({ mB: 's' })}
                                    required={subjectAndInternalRequired}
                                    disabled={
                                        !isWorkOrderStatusOnFormation ||
                                        isContractStatusDelayed ||
                                        isContractStatusCompleted
                                    }
                                    rules={subjectAndInternalRequired ? requiredBaseRule : undefined}
                                />
                                <ControlledTextField
                                    control={control}
                                    label="Внутренний номер"
                                    tooltipSize="m"
                                    name="internalNumber"
                                    className={cnMixSpace({ mB: 's' })}
                                    required={subjectAndInternalRequired}
                                    disabled={
                                        !isWorkOrderStatusOnFormation ||
                                        isContractStatusDelayed ||
                                        isContractStatusCompleted
                                    }
                                    rules={subjectAndInternalRequired ? requiredBaseRule : undefined}
                                />
                                <ControlledTextField
                                    control={control}
                                    label="Подрядчик"
                                    name="contractor"
                                    className={cnMixSpace({ mB: 's' })}
                                    disabled
                                />
                                <ControlledDateField
                                    control={control}
                                    label="Дата выпуска"
                                    name="signingDate"
                                    tooltipSize="m"
                                    required
                                    className={cnMixSpace({ mB: 'l' })}
                                    rules={{
                                        validate: (value) => {
                                            if (!value) {
                                                return 'Поле обязательно для заполнения';
                                            }
                                            clearErrors('signingDate');

                                            return true;
                                        },
                                    }}
                                    disabled={
                                        !isWorkOrderStatusOnFormation ||
                                        isContractStatusDelayed ||
                                        isContractStatusCompleted
                                    }
                                    rightSide={IconCalendar}
                                />
                            </>
                        )}
                        {preparedDefaultRequisitesData?.workOrderChange && (
                            <>
                                <GroupHeader mB="s">Данные Изменения к НЗ</GroupHeader>

                                <ControlledTextField
                                    control={control}
                                    label="Номер Изм к НЗ"
                                    name="workOrderChange.internalNumber"
                                    className={cnMixSpace({ mB: 'l' })}
                                    disabled
                                />

                                <ControlledDateField
                                    control={control}
                                    label="Дата подписания"
                                    name="workOrderChange.signingDate"
                                    className={cnMixSpace({ mB: 'l' })}
                                    disabled
                                />
                            </>
                        )}
                        {!!preparedDefaultRequisitesData?.seasons?.length && (
                            <>
                                <GroupHeader mB="s">% перераспределения Итогов</GroupHeader>

                                {(preparedDefaultRequisitesData.seasons as BimWorkOrderSeasonData[])
                                    .slice()
                                    .sort(({ year: yearA }, { year: yearB }) => yearA - yearB)
                                    .map((year) => (
                                        <ControlledTextField
                                            key={year.id}
                                            control={control}
                                            placeholder="%"
                                            type="text"
                                            label={`${year.year}`}
                                            name={`season_${year.workOrderSeasonId}`}
                                            className={cnMixSpace({ mB: 's' })}
                                            disabled
                                        />
                                    ))}
                            </>
                        )}
                        {isFrameContract && (
                            <>
                                <GroupHeader mB="s">Сумма Наряд-Заказа</GroupHeader>
                                <ControlledTextField
                                    control={control}
                                    label="Сумма с НДС (руб.)"
                                    name="amountWithTax"
                                    className={cnMixSpace({ mB: 'l' })}
                                    disabled
                                />
                                <ControlledTextField
                                    control={control}
                                    label="Сумма без НДС (руб.)"
                                    name="amountWithoutTax"
                                    className={cnMixSpace({ mB: 'l' })}
                                    disabled
                                />
                                <ControlledTextField
                                    control={control}
                                    label="НДС, 20% (руб.)"
                                    name="tax"
                                    className={cnMixSpace({ mB: 'l' })}
                                    disabled
                                />
                                <ControlledTextField
                                    control={control}
                                    label="Остаток без НДС (руб.)"
                                    name="remainder"
                                    className={cnMixSpace({ mB: 'l' })}
                                    disabled
                                />
                                <GroupHeader mB="s">Производство работ</GroupHeader>
                                <ControlledDateField
                                    control={control}
                                    label="Дата начала и окончания"
                                    name="rangeDate"
                                    tooltipSize="m"
                                    type="date-range"
                                    required
                                    disabled={
                                        !isWorkOrderStatusOnFormation ||
                                        isContractStatusDelayed ||
                                        isContractStatusCompleted
                                    }
                                    rightSide={[IconCalendar, IconCalendar]}
                                    rules={{
                                        validate: (value) => {
                                            if (!value || value.includes(undefined) || value.includes(null)) {
                                                return 'Поле обязательно для заполнения';
                                            }

                                            return true;
                                        },
                                    }}
                                />
                            </>
                        )}
                    </GridItem>
                </Grid>
            </form>

            <ModalDialog
                title={modalStatusTitle}
                isOpen={isOpenDialogStatus && !!confirmChangeStatusText}
                onConfirm={handleSubmitDialogStatus}
                onClose={handleCancelDialogStatus}
                btnConfirmLabel="Изменить"
                btnCancelLabel="Не изменять"
                withTitleCloseButton
                bodyMinHeight="max-content"
            >
                <div style={{ padding: 20, lineHeight: '24px', width: 520 }}>{confirmChangeStatusText}</div>
            </ModalDialog>

            <ConfirmationDialog
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить?  Данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </ModalDialog>
    );
};

BimOrderRequisitesUpdate.displayName = 'BimOrderRequisitesUpdate';
